import { observer } from 'mobx-react-lite';
import { selectEventCharmLabel } from './CharmPicker.locstring.json';
import loc from 'owa-localize';
import * as React from 'react';
import { CharmDefaultSet } from '../index';
import type ColorScheme from 'owa-color-utils/lib/ColorScheme';

import {
    areDefaultCharmsAvailable,
    CharmInfo,
    charmsDatapoints,
    getCharmForId,
    getCharmForIdOrUnsetCharm,
    getNoCharmInfoObject,
    CHARMS_USAGE_GETDEFAULTSET,
    CHARMS_USAGE_SOURCE_CHARMPICKERANCHORBUTTON,
} from 'owa-calendar-charms-store';
import { Callout, DirectionalHint } from '@fluentui/react/lib/Callout';
import { CharmControl, CharmControlColorProps, CharmPickerBackground } from 'owa-charmcontrol';
import type { CharmPickerPropsBase } from './CharmPickerPropsBase';
import { CommandButton, IButton, IButtonStyles } from '@fluentui/react/lib/Button';
import { DefaultPalette } from '@fluentui/style-utilities/lib/index';
import { logUsage } from 'owa-analytics';
import { ControlIcons } from 'owa-control-icons';
import { Icon } from '@fluentui/react/lib/Icon';
import isNewDefaultCharmIconEnabled from 'owa-charmcontrol/lib/utils/isNewDefaultCharmIconEnabled';

import styles from './CharmPicker.scss';

export interface CharmPickerProps extends CharmPickerPropsBase {
    selectedCharm: CharmInfo;
}

export interface CharmPickerHandle {
    focus(): void;
}

export default observer(
    function CharmPicker(props: CharmPickerProps, ref: React.Ref<CharmPickerHandle>) {
        const charmButton = React.useRef<IButton>();
        const charmButtonContainer = React.useRef<HTMLDivElement>();
        const [showCallout, setShowCallout] = React.useState(false);
        React.useEffect(() => {
            // If CharmPicker's menu is closed, run onAfterMenuDismiss
            if (!showCallout) {
                props.onAfterMenuDismiss?.();
            }
        }, [showCallout]);
        React.useImperativeHandle(
            ref,
            () => ({
                focus() {
                    charmButton.current.focus();
                },
            }),
            []
        );
        /**
         * Get the icon to show in the guided input box, if there isn't an icon selected yet, return the dot icon
         */
        const getDefaultIcon = (icon: number): CharmInfo => {
            return getCharmForIdOrUnsetCharm(icon);
        };
        /**
         * Select an icon
         */
        const onDismiss = () => {
            setShowCallout(false);
        };
        /**
         * Select an icon
         */
        const onSelectIcon = (icon: CharmInfo) => {
            setShowCallout(false);
            let { onSelectIcon } = props;
            if (onSelectIcon) {
                onSelectIcon(icon);
            }
        };
        /**
         * Updates the store when icon is clicked to show or hide the icon picker
         */
        const charmAnchorButtonClicked = () => {
            const { customData } = charmsDatapoints.CharmsDatapoint.charmdefaultsetdatapoint;
            logUsage(
                CHARMS_USAGE_GETDEFAULTSET,
                customData(CHARMS_USAGE_SOURCE_CHARMPICKERANCHORBUTTON)
            );
            if (props.onClick) {
                props.onClick();
            }
            setShowCallout(!showCallout);
        };
        const calculateButtonStyle = (colors: ColorScheme, background: CharmPickerBackground) => {
            let textColor = colors
                ? colors.accessibleText.calPrimaryBackground
                : DefaultPalette.white;
            if (colors && background == CharmPickerBackground.Light) {
                textColor = colors.accessibleText.msColorWhiteBackground;
            }
            let customStyle: IButtonStyles = {
                root: {
                    height: '100%',
                    width: '100%',
                    padding: '0px',
                    borderRadius: '2px',
                    marginLeft: '1px',
                    color: textColor,
                },
                flexContainer: {
                    justifyContent: 'center',
                },
                rootChecked: {
                    backgroundColor:
                        background == CharmPickerBackground.CalendarColor
                            ? colors.calLight
                            : colors.calLighter,
                },
                rootCheckedHovered: {
                    backgroundColor: colors.calLight,
                },
                rootHovered: {
                    backgroundColor:
                        background == CharmPickerBackground.CalendarColor
                            ? colors.calLighter
                            : DefaultPalette.neutralLighter,
                },
                rootPressed: {
                    backgroundColor:
                        background == CharmPickerBackground.CalendarColor
                            ? colors.calLight
                            : DefaultPalette.neutralLight,
                },
            };
            const iconStyle = {
                color: textColor,
            };
            return { customStyle: customStyle, iconStyle: iconStyle };
        };
        let isCharmPickerButtonEnabled = !props.disabled && areDefaultCharmsAvailable();
        let { customStyle, iconStyle } = calculateButtonStyle(props.colorScheme, props.background);
        let selectedIconId = 0;
        const selectedIcon = getDefaultIcon(props.selectedCharm.IconId);
        if (selectedIcon != null) {
            selectedIconId = selectedIcon.IconId;
        }
        let anchorCharm = getCharmForId(selectedIconId);
        let anchorCharmId: number = anchorCharm.IconId;
        let unsetCharm = getNoCharmInfoObject();
        let anchorCharmLabelText =
            anchorCharm.IconId == unsetCharm.IconId
                ? loc(selectEventCharmLabel)
                : anchorCharm.LocalizedName;
        let primaryColor = props.colorScheme.calPrimary;
        if (props.colorScheme && props.background == CharmPickerBackground.Light) {
            primaryColor = props.colorScheme.accessibleText.msColorWhiteBackground;
        }
        let colorProps: CharmControlColorProps = {
            colorPattern: props.colorPattern,
            calPrimary: primaryColor,
            calDark: props.colorScheme.calDark,
            calDarkAlt: props.colorScheme.calDarkAlt,
            textColor: props.colorScheme.accessibleText.calPrimaryBackground,
        };
        const showDefaultCharmIcon = isNewDefaultCharmIconEnabled() && anchorCharmId === 0;
        const getCharmIcon = () => {
            return showDefaultCharmIcon ? (
                <Icon iconName={ControlIcons.EmojiTabSymbols} style={iconStyle} />
            ) : (
                <CharmControl
                    iconId={anchorCharmId}
                    className={
                        props.charmClassName != null ? props.charmClassName : styles.charmIcon
                    }
                    colorProps={colorProps}
                />
            );
        };

        return (
            <div ref={charmButtonContainer}>
                <CommandButton
                    componentRef={charmButton}
                    ariaLabel={anchorCharmLabelText}
                    title={anchorCharmLabelText}
                    onClick={charmAnchorButtonClicked}
                    styles={customStyle}
                    disabled={!isCharmPickerButtonEnabled}
                    checked={showCallout}
                    className={props.buttonClassName}>
                    {getCharmIcon()}
                    {showCallout && (
                        <Callout
                            className={styles.callout}
                            directionalHint={DirectionalHint.bottomLeftEdge}
                            isBeakVisible={false}
                            target={charmButtonContainer}
                            setInitialFocus={true}
                            onDismiss={onDismiss}>
                            <CharmDefaultSet
                                /* style={colorSchemeLightColorStyle} //TODO: Uncomment when we fix 'bug 18609 - 'Incorrect component selector (focus, hover, active) colors' */
                                selectedIcon={selectedIcon}
                                onSelectIcon={onSelectIcon}
                                color={props.colorScheme}
                                containerClassName={props.resultContainerClassName}
                                trapFocus={true}
                            />
                        </Callout>
                    )}
                </CommandButton>
            </div>
        );
    },
    { forwardRef: true }
);
